import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  Save, RefreshCw, Upload, Globe, Search, Image as ImageIcon, 
  MapPin, Phone, Mail, Building, Eye, ExternalLink, Check, AlertCircle
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CMSSeoPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [seoData, setSeoData] = useState({
    // General SEO
    site_name: "L'Agence MSC",
    site_tagline: "Mon Service Com",
    meta_title: "L'Agence MSC | Agence de Communication à Istres",
    meta_description: "L'Agence MSC (Mon Service Com) - Votre agence de communication à Istres (13800). Création graphique, production vidéo, sites web et marketing digital.",
    meta_keywords: "agence communication, Istres, Bouches-du-Rhône, création graphique, production vidéo, site web, marketing digital",
    
    // Location
    address_street: "",
    address_city: "Istres",
    address_postal: "13800",
    address_region: "Bouches-du-Rhône",
    address_country: "France",
    geo_lat: "43.5138",
    geo_lng: "4.9878",
    
    // Contact
    contact_email: "contact@agence-msc.fr",
    contact_phone: "",
    
    // Social Media
    social_facebook: "",
    social_twitter: "",
    social_linkedin: "",
    social_instagram: "",
    
    // Favicon & Logos
    favicon_url: "/favicon.ico",
    logo_192_url: "/logo192.png",
    logo_512_url: "/logo512.png",
    apple_touch_icon_url: "/apple-touch-icon.png",
    og_image_url: "/logo512.png",
    
    // Colors
    theme_color: "#1a365d",
    background_color: "#ffffff",
    
    // Advanced
    google_site_verification: "",
    bing_site_verification: "",
    canonical_url: "https://agence-msc.fr"
  });

  useEffect(() => {
    loadSeoData();
  }, []);

  const loadSeoData = async () => {
    try {
      const res = await api.get('/cms/seo-settings');
      if (res.data) {
        setSeoData(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.log('No SEO settings yet, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/seo-settings', seoData);
      toast.success('Paramètres SEO enregistrés !');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'seo');
    
    // Don't convert ICO files to WebP
    const isIco = file.name.toLowerCase().endsWith('.ico');
    formData.append('convert_webp', isIco ? 'false' : 'true');

    try {
      const res = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSeoData(prev => ({ ...prev, [field]: res.data.url }));
      toast.success('Image uploadée avec succès !');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const updateField = (field, value) => {
    setSeoData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate title length indicator
  const titleLength = seoData.meta_title?.length || 0;
  const titleStatus = titleLength < 30 ? 'short' : titleLength > 60 ? 'long' : 'good';
  
  // Calculate description length indicator
  const descLength = seoData.meta_description?.length || 0;
  const descStatus = descLength < 120 ? 'short' : descLength > 160 ? 'long' : 'good';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Search className="w-6 h-6 text-cyan-500" />
            Référencement SEO
          </h1>
          <p className="text-slate-500">Gérez les paramètres SEO, favicon et informations de l&apos;entreprise</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-gradient-to-r from-cyan-500 to-blue-600"
        >
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="general" className="gap-2">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Général</span>
          </TabsTrigger>
          <TabsTrigger value="location" className="gap-2">
            <MapPin className="w-4 h-4" />
            <span className="hidden sm:inline">Localisation</span>
          </TabsTrigger>
          <TabsTrigger value="images" className="gap-2">
            <ImageIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Images</span>
          </TabsTrigger>
          <TabsTrigger value="preview" className="gap-2">
            <Eye className="w-4 h-4" />
            <span className="hidden sm:inline">Aperçu</span>
          </TabsTrigger>
        </TabsList>

        {/* General SEO Tab */}
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informations générales</CardTitle>
              <CardDescription>Ces informations apparaîtront dans les résultats de recherche Google</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nom du site</Label>
                  <Input 
                    value={seoData.site_name}
                    onChange={(e) => updateField('site_name', e.target.value)}
                    placeholder="L'Agence MSC"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slogan / Tagline</Label>
                  <Input 
                    value={seoData.site_tagline}
                    onChange={(e) => updateField('site_tagline', e.target.value)}
                    placeholder="Mon Service Com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Titre SEO (balise title)</Label>
                  <Badge variant={titleStatus === 'good' ? 'default' : 'destructive'} className={titleStatus === 'good' ? 'bg-green-500' : ''}>
                    {titleLength}/60 caractères
                    {titleStatus === 'good' && <Check className="w-3 h-3 ml-1" />}
                    {titleStatus !== 'good' && <AlertCircle className="w-3 h-3 ml-1" />}
                  </Badge>
                </div>
                <Input 
                  value={seoData.meta_title}
                  onChange={(e) => updateField('meta_title', e.target.value)}
                  placeholder="L'Agence MSC | Agence de Communication à Istres"
                  className={titleStatus === 'long' ? 'border-red-300' : titleStatus === 'good' ? 'border-green-300' : ''}
                />
                <p className="text-xs text-slate-500">
                  Recommandé : 50-60 caractères. Ce titre apparaît en bleu dans Google.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Description SEO (meta description)</Label>
                  <Badge variant={descStatus === 'good' ? 'default' : 'destructive'} className={descStatus === 'good' ? 'bg-green-500' : ''}>
                    {descLength}/160 caractères
                    {descStatus === 'good' && <Check className="w-3 h-3 ml-1" />}
                    {descStatus !== 'good' && <AlertCircle className="w-3 h-3 ml-1" />}
                  </Badge>
                </div>
                <Textarea 
                  value={seoData.meta_description}
                  onChange={(e) => updateField('meta_description', e.target.value)}
                  placeholder="Description de votre entreprise..."
                  rows={3}
                  className={descStatus === 'long' ? 'border-red-300' : descStatus === 'good' ? 'border-green-300' : ''}
                />
                <p className="text-xs text-slate-500">
                  Recommandé : 150-160 caractères. Cette description apparaît sous le titre dans Google.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Mots-clés (séparés par des virgules)</Label>
                <Textarea 
                  value={seoData.meta_keywords}
                  onChange={(e) => updateField('meta_keywords', e.target.value)}
                  placeholder="agence communication, Istres, création graphique..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>URL canonique</Label>
                <Input 
                  value={seoData.canonical_url}
                  onChange={(e) => updateField('canonical_url', e.target.value)}
                  placeholder="https://agence-msc.fr"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Vérification Google (meta tag)</Label>
                  <Input 
                    value={seoData.google_site_verification}
                    onChange={(e) => updateField('google_site_verification', e.target.value)}
                    placeholder="google-site-verification=..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vérification Bing (meta tag)</Label>
                  <Input 
                    value={seoData.bing_site_verification}
                    onChange={(e) => updateField('bing_site_verification', e.target.value)}
                    placeholder="msvalidate.01=..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Location Tab */}
        <TabsContent value="location" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-500" />
                Informations de l&apos;entreprise
              </CardTitle>
              <CardDescription>Ces informations améliorent le référencement local</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input 
                  value={seoData.address_street}
                  onChange={(e) => updateField('address_street', e.target.value)}
                  placeholder="123 Rue Exemple"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input 
                    value={seoData.address_city}
                    onChange={(e) => updateField('address_city', e.target.value)}
                    placeholder="Istres"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input 
                    value={seoData.address_postal}
                    onChange={(e) => updateField('address_postal', e.target.value)}
                    placeholder="13800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Région / Département</Label>
                  <Input 
                    value={seoData.address_region}
                    onChange={(e) => updateField('address_region', e.target.value)}
                    placeholder="Bouches-du-Rhône"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input 
                    value={seoData.address_country}
                    onChange={(e) => updateField('address_country', e.target.value)}
                    placeholder="France"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Latitude (pour Google Maps)</Label>
                  <Input 
                    value={seoData.geo_lat}
                    onChange={(e) => updateField('geo_lat', e.target.value)}
                    placeholder="43.5138"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Longitude</Label>
                  <Input 
                    value={seoData.geo_lng}
                    onChange={(e) => updateField('geo_lng', e.target.value)}
                    placeholder="4.9878"
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contact
                </h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Email de contact</Label>
                    <Input 
                      type="email"
                      value={seoData.contact_email}
                      onChange={(e) => updateField('contact_email', e.target.value)}
                      placeholder="contact@agence-msc.fr"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input 
                      value={seoData.contact_phone}
                      onChange={(e) => updateField('contact_phone', e.target.value)}
                      placeholder="+33 4 XX XX XX XX"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Images Tab */}
        <TabsContent value="images" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Favicon et Logos</CardTitle>
              <CardDescription>Ces images apparaissent dans les onglets du navigateur et les résultats de recherche</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {uploading && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-blue-700">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Upload en cours...</span>
                </div>
              )}
              
              {/* Favicon */}
              <div className="p-4 border rounded-lg space-y-3 hover:border-cyan-300 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Favicon</Label>
                    <p className="text-xs text-slate-500">Icône affichée dans l&apos;onglet du navigateur (PNG ou ICO, 32x32 recommandé)</p>
                  </div>
                  <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                    {seoData.favicon_url ? (
                      <img 
                        src={seoData.favicon_url.startsWith('http') ? seoData.favicon_url : `${process.env.REACT_APP_BACKEND_URL}${seoData.favicon_url}`}
                        alt="Favicon"
                        className="w-8 h-8 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={seoData.favicon_url}
                    onChange={(e) => updateField('favicon_url', e.target.value)}
                    placeholder="/favicon.ico ou URL complète"
                    className="flex-1"
                  />
                  <div className="relative">
                    <input 
                      type="file"
                      id="favicon-upload"
                      accept=".ico,.png,.jpg,.jpeg,.webp"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      onChange={(e) => handleUpload(e, 'favicon_url')}
                    />
                    <Button variant="outline" disabled={uploading} className="pointer-events-none">
                      {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Logo 192 */}
              <div className="p-4 border rounded-lg space-y-3 hover:border-cyan-300 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Logo 192x192 (PNG)</Label>
                    <p className="text-xs text-slate-500">Pour les appareils mobiles et PWA</p>
                  </div>
                  <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center">
                    {seoData.logo_192_url ? (
                      <img 
                        src={seoData.logo_192_url.startsWith('http') ? seoData.logo_192_url : `${process.env.REACT_APP_BACKEND_URL}${seoData.logo_192_url}`}
                        alt="Logo 192"
                        className="w-12 h-12 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={seoData.logo_192_url}
                    onChange={(e) => updateField('logo_192_url', e.target.value)}
                    placeholder="/logo192.png"
                    className="flex-1"
                  />
                  <div className="relative">
                    <input 
                      type="file"
                      id="logo192-upload"
                      accept=".png,.jpg,.jpeg,.webp"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      onChange={(e) => handleUpload(e, 'logo_192_url')}
                    />
                    <Button variant="outline" disabled={uploading} className="pointer-events-none">
                      {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Logo 512 */}
              <div className="p-4 border rounded-lg space-y-3 hover:border-cyan-300 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Logo 512x512 (PNG)</Label>
                    <p className="text-xs text-slate-500">Pour le partage sur réseaux sociaux (Open Graph)</p>
                  </div>
                  <div className="w-20 h-20 bg-slate-100 rounded flex items-center justify-center">
                    {seoData.logo_512_url ? (
                      <img 
                        src={seoData.logo_512_url.startsWith('http') ? seoData.logo_512_url : `${process.env.REACT_APP_BACKEND_URL}${seoData.logo_512_url}`}
                        alt="Logo 512"
                        className="w-16 h-16 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <ImageIcon className="w-10 h-10 text-slate-400" />
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={seoData.logo_512_url}
                    onChange={(e) => updateField('logo_512_url', e.target.value)}
                    placeholder="/logo512.png"
                    className="flex-1"
                  />
                  <div className="relative">
                    <input 
                      type="file"
                      id="logo512-upload"
                      accept=".png,.jpg,.jpeg,.webp"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      onChange={(e) => handleUpload(e, 'logo_512_url')}
                    />
                    <Button variant="outline" disabled={uploading} className="pointer-events-none">
                      {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Apple Touch Icon */}
              <div className="p-4 border rounded-lg space-y-3 hover:border-cyan-300 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Apple Touch Icon (180x180)</Label>
                    <p className="text-xs text-slate-500">Pour les raccourcis iOS</p>
                  </div>
                  <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center">
                    {seoData.apple_touch_icon_url ? (
                      <img 
                        src={seoData.apple_touch_icon_url.startsWith('http') ? seoData.apple_touch_icon_url : `${process.env.REACT_APP_BACKEND_URL}${seoData.apple_touch_icon_url}`}
                        alt="Apple Touch Icon"
                        className="w-12 h-12 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={seoData.apple_touch_icon_url}
                    onChange={(e) => updateField('apple_touch_icon_url', e.target.value)}
                    placeholder="/apple-touch-icon.png"
                    className="flex-1"
                  />
                  <div className="relative">
                    <input 
                      type="file"
                      id="apple-upload"
                      accept=".png,.jpg,.jpeg,.webp"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      onChange={(e) => handleUpload(e, 'apple_touch_icon_url')}
                    />
                    <Button variant="outline" disabled={uploading} className="pointer-events-none">
                      {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Theme Colors */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Couleur du thème (barre du navigateur mobile)</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="color"
                      value={seoData.theme_color}
                      onChange={(e) => updateField('theme_color', e.target.value)}
                      className="w-12 h-10 p-1"
                    />
                    <Input 
                      value={seoData.theme_color}
                      onChange={(e) => updateField('theme_color', e.target.value)}
                      placeholder="#1a365d"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Couleur de fond</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="color"
                      value={seoData.background_color}
                      onChange={(e) => updateField('background_color', e.target.value)}
                      className="w-12 h-10 p-1"
                    />
                    <Input 
                      value={seoData.background_color}
                      onChange={(e) => updateField('background_color', e.target.value)}
                      placeholder="#ffffff"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-green-500" />
                Aperçu Google
              </CardTitle>
              <CardDescription>Voici comment votre site apparaîtra dans les résultats de recherche</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Google Search Result Preview */}
              <div className="p-6 bg-white rounded-lg border shadow-sm max-w-2xl">
                <div className="space-y-1">
                  {/* Favicon + URL line */}
                  <div className="flex items-center gap-2 text-sm">
                    {seoData.favicon_url && (
                      <img 
                        src={seoData.favicon_url.startsWith('http') ? seoData.favicon_url : `${process.env.REACT_APP_BACKEND_URL}${seoData.favicon_url}`}
                        alt=""
                        className="w-4 h-4"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span className="text-slate-600">{seoData.canonical_url || 'https://agence-msc.fr'}</span>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-xl text-blue-700 hover:underline cursor-pointer font-normal">
                    {seoData.meta_title || 'Titre de votre page'}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {seoData.meta_description || 'Description de votre page...'}
                  </p>
                </div>
              </div>

              {/* Local Business Preview */}
              <div className="mt-6">
                <h4 className="font-medium mb-3">Aperçu fiche entreprise (Google Business)</h4>
                <div className="p-4 bg-white rounded-lg border shadow-sm max-w-md">
                  <div className="flex gap-4">
                    {seoData.logo_192_url && (
                      <img 
                        src={seoData.logo_192_url.startsWith('http') ? seoData.logo_192_url : `${process.env.REACT_APP_BACKEND_URL}${seoData.logo_192_url}`}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div>
                      <h4 className="font-semibold text-lg">{seoData.site_name}</h4>
                      <p className="text-sm text-slate-500">{seoData.site_tagline}</p>
                      <div className="flex items-center gap-1 text-sm text-slate-600 mt-1">
                        <MapPin className="w-3 h-3" />
                        {seoData.address_city}, {seoData.address_region}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Facebook/LinkedIn Preview */}
              <div className="mt-6">
                <h4 className="font-medium mb-3">Aperçu partage Facebook/LinkedIn</h4>
                <div className="max-w-lg border rounded-lg overflow-hidden bg-white shadow-sm">
                  {seoData.logo_512_url && (
                    <img 
                      src={seoData.logo_512_url.startsWith('http') ? seoData.logo_512_url : `${process.env.REACT_APP_BACKEND_URL}${seoData.logo_512_url}`}
                      alt=""
                      className="w-full h-48 object-cover bg-slate-100"
                      onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 200"><rect fill="%23f1f5f9" width="400" height="200"/><text x="50%" y="50%" font-family="Arial" font-size="14" fill="%2394a3b8" text-anchor="middle">Image de partage</text></svg>'; }}
                    />
                  )}
                  <div className="p-3">
                    <p className="text-xs text-slate-400 uppercase">{seoData.canonical_url?.replace('https://', '')}</p>
                    <h4 className="font-semibold text-slate-900">{seoData.meta_title}</h4>
                    <p className="text-sm text-slate-500 line-clamp-2">{seoData.meta_description}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <h4 className="font-medium text-amber-800 mb-2">💡 Conseils SEO</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Le titre doit contenir votre nom d&apos;entreprise et votre localisation</li>
                <li>• La description doit inciter à cliquer (appel à l&apos;action)</li>
                <li>• Utilisez des mots-clés pertinents pour votre activité et votre zone géographique</li>
                <li>• Assurez-vous que vos images sont au bon format (PNG, taille correcte)</li>
                <li>• Remplissez les informations de localisation pour le SEO local</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CMSSeoPage;
